import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
  memo,
} from 'react';
import { cx } from '@trinity/chat-components';
import { FocusableAnimation } from './FocusableAnimation';
import type { FocusableClasses } from './interfaces';
import styles from './style.module.css';

type Props = {
  autoFocus?: boolean;
  children: React.ReactNode;
  className?: string;
  classes?: FocusableClasses;
  disabled?: boolean;
  onBlur?: () => unknown;
  onFocus?: () => unknown;
};

type FocusableRefObject = {
  blur: () => unknown;
  focus: () => unknown;
};

const Focusable = ({
  autoFocus = false,
  classes = {},
  disabled,
  onBlur,
  onFocus,
  ...props
}: Props, ref: React.Ref<FocusableRefObject>) => {
  const [focused, setFocus] = useState(autoFocus);
  const focusRef = useRef<HTMLDivElement>(null);

  const handleBlur = useCallback(() => {
    setFocus(false);
    onBlur?.();
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    setFocus(true);
    onFocus?.();
  }, [disabled, onFocus]);

  useImperativeHandle(ref, () => {
    return {
      blur: handleBlur,
      focus: handleFocus,
    };
  }, [handleBlur, handleFocus]);

  const classnames = useMemo(() => {
    return {
      root: cx(styles.root, classes.root, props.className),
    };
  }, [
    classes.root,
    props.className,
  ]);

  return (
    <div
      className={classnames.root}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={focusRef}
      tabIndex={0}>
      {props.children}
      <FocusableAnimation
        classes={classes}
        focused={focused} />
    </div>
  );
};

const FocusableRef = memo(forwardRef<FocusableRefObject, Props>(Focusable));

export { FocusableRef as Focusable };
export default FocusableRef;